import * as React from 'react';
import { LocalDate, TemporalAdjusters } from 'js-joda';
import { isEmpty } from 'es-toolkit/compat';
import clsx from 'clsx';

import { DateAvailabilityObject } from 'types';
import { findFocusDate, range } from 'utils/calendar';
import ConsumerCalendarMonthSmartTabbing from 'components/calendar/ConsumerCalendarMonthSmartTabbing';

interface Props {
  availabilityOverride?: boolean;
  availableDates?: DateAvailabilityObject;
  endDate?: LocalDate;
  ignoreYear?: boolean;
  initializeToFirstOffered?: boolean;
  lockedDates?: DateAvailabilityObject;
  modifierClassName?: string;
  offeredDates?: DateAvailabilityObject;
  onSelect(date: string): void;
  openDates?: DateAvailabilityObject;
  selectedDate?: string;
  soldDates?: DateAvailabilityObject;
  startDate?: LocalDate;
  /**
   * This component implements two focus behaviors:
   *  A) Auto-focus a default date (probably the same as selectedDate) when the component mounts
   *     It will also set month-level focus on the relevant month, if month smart-tabbing behavior
   *     is enabled.
   *  B) Do nothing with focus at all
   *  Set autoFocusOnMount to true to get behavior A.
   */
  autoFocusOnMount: boolean;
  wideView?: boolean;
  includeAccessKey?: boolean;
}

export type ConsumerCalendarProps = Props;

export const ConsumerCalendar: React.FunctionComponent<Props> = ({
  availabilityOverride,
  availableDates = {},
  endDate,
  ignoreYear,
  initializeToFirstOffered,
  lockedDates = {},
  modifierClassName,
  offeredDates = {},
  onSelect,
  openDates = {},
  selectedDate,
  soldDates = {},
  autoFocusOnMount,
  startDate,
  wideView = true,
  includeAccessKey = false,
}) => {
  let defaultSelectedDate = LocalDate.now();
  const { min } = range(offeredDates);
  if (selectedDate) {
    defaultSelectedDate = LocalDate.parse(selectedDate);
  } else if (min && initializeToFirstOffered) {
    defaultSelectedDate = LocalDate.parse(min);
  }

  const [displayedMonth, setDisplayedMonth] = React.useState<LocalDate>(
    defaultSelectedDate.with(TemporalAdjusters.firstDayOfMonth())
  );
  const [focusDate, setFocusDate] = React.useState<LocalDate | undefined>(
    autoFocusOnMount ? defaultSelectedDate : undefined
  );
  const [activeMonthForDayTabbing, setActiveMonthForDayTabbing] = React.useState<
    'calendar-first' | 'calendar-next' | undefined
  >(autoFocusOnMount ? 'calendar-first' : undefined);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, date: string) => {
    const newDate = findFocusDate(event, date);
    if (newDate) {
      setFocusDate(newDate);
      // if the date is the same, then it was selected
      if (newDate.equals(LocalDate.parse(date))) {
        handleSelect(date);
      }
    }
  };

  const handleSelect = (date: string) => {
    onSelect(date);
  };

  const handleActivateDate = (date?: string, monthId?: 'calendar-first' | 'calendar-next') => {
    if (!date) {
      return;
    }
    setFocusDate(LocalDate.parse(date));
    setActiveMonthForDayTabbing(monthId);
  };

  const handleTabAwayFromDays = () => {
    setActiveMonthForDayTabbing(undefined);
  };

  const handleClickPrev = () => {
    setDisplayedMonth(displayedMonth.minusMonths(1));
  };

  const handleClickNext = () => {
    setDisplayedMonth(displayedMonth.plusMonths(1));
  };

  let maxLocalDate;
  let minLocalDate;
  let nextDisabled;
  let prevDisabled;

  if (offeredDates) {
    const { min, max } = range(offeredDates);
    minLocalDate = min ? LocalDate.parse(min) : LocalDate.now();
    maxLocalDate = max ? LocalDate.parse(max) : LocalDate.now();
    nextDisabled = !max || displayedMonth.plusMonths(1).isAfter(maxLocalDate);
    prevDisabled = !min || displayedMonth.minusMonths(1).isBefore(minLocalDate.minusMonths(1));
  }

  if (isEmpty(offeredDates)) {
    minLocalDate = startDate;
    maxLocalDate = endDate;
    if (maxLocalDate && minLocalDate) {
      nextDisabled = displayedMonth.plusMonths(1).isAfter(maxLocalDate);
      prevDisabled = displayedMonth.minusMonths(1).isBefore(minLocalDate.minusMonths(1));
    }
  }

  const className = clsx('ConsumerCalendar', modifierClassName, {
    'ConsumerCalendar--double': wideView,
    'ConsumerCalendar--single': !wideView,
  });

  const passedProps = {
    availabilityOverride,
    availableDates,
    endDate,
    ignoreYear,
    initializeToFirstOffered,
    lockedDates,
    modifierClassName,
    offeredDates,
    onSelect,
    openDates,
    selectedDate,
    soldDates,
    startDate,
    wideView,
  };

  const selectedDates = selectedDate ? [selectedDate] : [];
  const sharedMonthProps = {
    ...passedProps,
    disableNext: nextDisabled,
    disablePrev: prevDisabled,
    focusDate,
    handleKeyDown,
    onClickNext: handleClickNext,
    onClickPrev: handleClickPrev,
    onSelectDate: handleSelect,
    selectedDates,
  };

  return (
    <div className={className} data-testid="consumer-calendar">
      <>
        <ConsumerCalendarMonthSmartTabbing
          {...sharedMonthProps}
          month={displayedMonth}
          monthId="calendar-first"
          isMonthActiveForDayTabbing={activeMonthForDayTabbing === 'calendar-first'}
          onActivateDate={handleActivateDate}
          onTabAwayFromDays={handleTabAwayFromDays}
          data-testid="calendar-first"
          includeAccessKey={includeAccessKey}
        />

        <ConsumerCalendarMonthSmartTabbing
          {...sharedMonthProps}
          month={displayedMonth.plusMonths(1)}
          monthId="calendar-next"
          isMonthActiveForDayTabbing={activeMonthForDayTabbing === 'calendar-next'}
          onActivateDate={handleActivateDate}
          onTabAwayFromDays={handleTabAwayFromDays}
          data-testid="calendar-next"
          includeAccessKey={false}
        />
      </>
    </div>
  );
};

export default ConsumerCalendar;
